<script lang="ts" setup>
import logo from '@/assets/logo.svg'
import { Link } from '@inertiajs/vue3'
import { ArrowUpRight, Facebook, Instagram, Linkedin, Mail, Phone } from 'lucide-vue-next'

withDefaults(
    defineProps<{
        dark?: boolean
    }>(),
    {
        dark: false,
    },
)
</script>

<template>
    <div>
        <div class="from-primary h-px bg-linear-to-l to-[#ce456b]"></div>
        <footer :data-dark="dark" class="group bg-white py-8 text-slate-800 data-[dark=true]:bg-[#0c0f10]">
            <div class="mx-auto flex w-full max-w-[1304px] flex-col justify-between gap-6 p-4 lg:flex-row lg:items-center">
                <div>
                    <img :src="logo" alt="Logo ILOS" />
                </div>
                <a
                    href="https://api.whatsapp.com/send/?phone=552134453000&text=Ol%C3%A1%21+Gostaria+de+mais+informa%C3%A7%C3%B5es+sobre+%20o+F%C3%B3rum+ILOS.&type=phone_number&app_absent=0"
                    target="_blank"
                    rel="noopener"
                    class="text-primary flex items-center gap-2 hover:underline"
                >
                    {{ $t('Talk to us') }} <ArrowUpRight
                /></a>

                <ul class="flex flex-col gap-x-6 gap-y-3 group-data-[dark=true]:text-white lg:flex-row lg:items-center">
                    <li>
                        <a
                            href="mailto:foruns@ilos.com.br"
                            target="_blank"
                            class="text-normal flex items-center gap-2 hover:[&>span]:scale-105 group-data-[dark=false]:hover:[&>span]:border-white group-data-[dark=false]:hover:[&>span]:shadow-lg group-data-[dark=false]:hover:[&>span]:shadow-slate-200 group-data-[dark=true]:hover:[&>span]:border-white group-data-[dark=true]:hover:[&>span]:bg-white group-data-[dark=true]:hover:[&>span]:text-slate-700"
                        >
                            <span
                                class="flex size-10 items-center justify-center rounded-full border transition-colors group-data-[dark=false]:border-slate-200 group-data-[dark=true]:border-slate-700"
                            >
                                <Mail class="size-5" />
                            </span>
                            foruns@ilos.com.br
                        </a>
                    </li>
                    <li>
                        <a
                            href="tel:551138471909"
                            target="_blank"
                            class="text-normal group flex items-center gap-2 hover:[&>span]:scale-105 group-data-[dark=false]:hover:[&>span]:border-white group-data-[dark=false]:hover:[&>span]:shadow-lg group-data-[dark=false]:hover:[&>span]:shadow-slate-200 group-data-[dark=true]:hover:[&>span]:border-white group-data-[dark=true]:hover:[&>span]:bg-white group-data-[dark=true]:hover:[&>span]:text-slate-700"
                        >
                            <span
                                class="flex size-10 items-center justify-center rounded-full border transition-colors group-data-[dark=false]:border-slate-200 group-data-[dark=true]:border-slate-700"
                            >
                                <Phone class="size-5" />
                            </span>
                            {{ $t('Phone 1') }}
                        </a>
                    </li>
                    <li>
                        <a
                            href="tel:552134453000"
                            class="text-normal group flex items-center gap-2 hover:[&>span]:scale-105 group-data-[dark=false]:hover:[&>span]:border-white group-data-[dark=false]:hover:[&>span]:shadow-lg group-data-[dark=false]:hover:[&>span]:shadow-slate-200 group-data-[dark=true]:hover:[&>span]:border-white group-data-[dark=true]:hover:[&>span]:bg-white group-data-[dark=true]:hover:[&>span]:text-slate-700"
                        >
                            <span
                                class="flex size-10 items-center justify-center rounded-full border transition-colors group-data-[dark=false]:border-slate-200 group-data-[dark=true]:border-slate-700"
                            >
                                <Phone class="size-5" />
                            </span>
                            {{ $t('Phone 2') }}
                        </a>
                    </li>
                </ul>
            </div>

            <hr class="container w-full border-gray-200 group-data-[dark=true]:border-[#202427]" />

            <div class="mx-auto flex w-full max-w-[1304px] flex-col justify-between gap-6 p-4 lg:flex-row lg:items-center">
                <div class="flex flex-col gap-3 gap-x-7 group-data-[dark=true]:text-white md:flex-row md:items-center">
                    <p class="text-sm md:text-base">© {{ new Date().getFullYear() }} ILOS -> {{ $t('All rights reserved') }}</p>
                    <p class="text-sm md:text-base">CNPJ: 07.639.095/0003-07</p>
                    <p class="text-sm underline">
                        <Link href="/privacy-policy">{{ $t('General terms, cancellation and privacy') }}</Link>
                    </p>
                </div>

                <ul class="flex items-center gap-x-4 group-data-[dark=true]:text-white">
                    <li>
                        <a
                            href="https://www.instagram.com/ilos.oficial/"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="text-normal group flex items-center gap-2 hover:underline hover:[&>span]:scale-105 group-data-[dark=false]:hover:[&>span]:border-white group-data-[dark=false]:hover:[&>span]:shadow-lg group-data-[dark=false]:hover:[&>span]:shadow-slate-200 group-data-[dark=true]:hover:[&>span]:border-white group-data-[dark=true]:hover:[&>span]:bg-white group-data-[dark=true]:hover:[&>span]:text-slate-700"
                        >
                            <span
                                class="flex size-10 items-center justify-center rounded-full border transition-colors group-data-[dark=false]:border-slate-200 group-data-[dark=true]:border-slate-700"
                            >
                                <Instagram class="size-5" />
                            </span>
                            <span class="sr-only">{{ $t('open_instagram') }}</span>
                        </a>
                    </li>
                    <li>
                        <a
                            href="https://www.facebook.com/ILOS.oficial"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="text-normal group flex items-center gap-2 hover:underline hover:[&>span]:scale-105 group-data-[dark=false]:hover:[&>span]:border-white group-data-[dark=false]:hover:[&>span]:shadow-lg group-data-[dark=false]:hover:[&>span]:shadow-slate-200 group-data-[dark=true]:hover:[&>span]:border-white group-data-[dark=true]:hover:[&>span]:bg-white group-data-[dark=true]:hover:[&>span]:text-slate-700"
                        >
                            <span
                                class="flex size-10 items-center justify-center rounded-full border transition-colors group-data-[dark=false]:border-slate-200 group-data-[dark=true]:border-slate-700"
                            >
                                <Facebook class="size-5" />
                            </span>
                            <span class="sr-only">{{ $t('open_instagram') }}</span>
                        </a>
                    </li>
                    <li>
                        <a
                            href="https://www.linkedin.com/company/ilosoficial/"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="text-normal group flex items-center gap-2 hover:underline hover:[&>span]:scale-105 group-data-[dark=false]:hover:[&>span]:border-white group-data-[dark=false]:hover:[&>span]:shadow-lg group-data-[dark=false]:hover:[&>span]:shadow-slate-200 group-data-[dark=true]:hover:[&>span]:border-white group-data-[dark=true]:hover:[&>span]:bg-white group-data-[dark=true]:hover:[&>span]:text-slate-700"
                        >
                            <span
                                class="flex size-10 items-center justify-center rounded-full border transition-colors group-data-[dark=false]:border-slate-200 group-data-[dark=true]:border-slate-700"
                            >
                                <Linkedin class="size-5" />
                            </span>
                            <span class="sr-only">{{ $t('open_instagram') }}</span>
                        </a>
                    </li>
                </ul>
            </div>
        </footer>
    </div>
</template>
